<template>
  <entity-list-layout
    class="sensor-list"
    :title="featureIsActive('monitoring_point') ? 'MONITORING POINT' : 'SENSOR'"
    :count="sensors.length"
    :query.sync="search"
    :search-hint="$t('search')"
  >
    <template #title-area-content>
      <v-btn-toggle
        v-if="featureIsActive('monitoring_point')"
        v-model="activeStatusFilter"
        mandatory
        dense
        color="primary"
        class="mr-4"
        data-testid="active-status-filter"
        :aria-label="$t('active_status_filter.aria_label')"
      >
        <v-btn small value="active" data-testid="active-status-filter-active">
          {{ $t('active_status_filter.active') }}
        </v-btn>
        <v-btn small value="inactive" data-testid="active-status-filter-inactive">
          {{ $t('active_status_filter.inactive') }}
        </v-btn>
        <v-btn small value="all" data-testid="active-status-filter-all">
          {{ $t('active_status_filter.all') }}
        </v-btn>
      </v-btn-toggle>
      <create-sensor-button v-if="creatable" />
    </template>
    <p-data-list :pagination.sync="pagination" :data="sensors" :getItemProps="genItemProps">
      <data-field name="No" headerClass="column--no">
        <template v-slot="props">{{ props.index + 1 }}</template>
      </data-field>
      <data-field
        :name="$t('field.status')"
        headerClass="column--status"
      >
        <template v-slot="props">
          <div class="text-sm-center">
            <sensor-status-indicator :sensor="props.item" />
          </div>
        </template>
      </data-field>
      <data-field
        :name="$mpt($t('field.bookmarked'))"
        value="bookmarked"
        headerClass="column--bookmarked"
        sortable

      >
        <template v-slot="props">
          <div class="text-sm-center">
            <toggle-icon-button
              icon="mdi-bookmark"
              data-testid="toggle-bookmark"
              :value="props.item.bookmarked"
              @input="bookmarked => updateBookmarked(props.item.id, bookmarked)"
            />
          </div>
        </template>
      </data-field>
      <data-field :name="$t('field.type')" value="type_index" headerClass="column--type" sortable>
        <template v-slot="props">
          <sensor-type-label :sensor="props.item" />
        </template>
      </data-field>
      <data-field :name="$mpt($t('field.identifier'))" value="_identifier_sort_key" headerClass="column--id" sortable>
        <template v-slot="props">
          <span
            v-if="showsIdentifier(props.item)"
            class="sensor-list__sensor-id"
          >
            {{ props.item.device_id }}
          </span>
        </template>
      </data-field>
      <data-field :name="$mpt($t('field.name'))" value="name" headerClass="column--name" sortable>
        <template v-slot="props">
          <router-link class="sensor-list__sensor-name" :to="{ name: 'sensor', params: { id: props.item.id }}">
            {{ props.item.name }}
            <template v-if="featureIsActive('monitoring_point') && props.item.status === 'inactive'">
              {{ $t('inactive_suffix') }}
            </template>
          </router-link>
        </template>
      </data-field>
      <data-field
        :name="$t('field.installation_date')"
        value="installed_on"
        headerClass="column--installed-on"
        sortable
      >
        <template v-slot="props">{{ formatDate(props.item.installed_on) }}</template>
      </data-field>
      <data-field :name="$t('field.threshold')" value="threshold" headerClass="column--threshold" sortable>
        <template v-slot="props">
          <div :style="props.item | thresholdStyle">
            {{ props.item | threshold }}
          </div>
        </template>
      </data-field>
      <data-field :name="$t('field.place')" value="place" headerClass="column--place" sortable>
        <template v-slot="props">
          <span class="sensor-list__installation-site">{{ props.item.place }}</span>
        </template>
      </data-field>
      <data-field
        :name="$t('field.organization_name')"
        value="created_by.organization_name"
        headerClass="column--organization"
        sortable
      >
        <template v-slot="props">{{ props.item.created_by.organization_name }}</template>
      </data-field>
      <data-field :name="$t('field.created_by')" value="created_by.name" headerClass="column--user" sortable>
        <template v-slot="props">{{ props.item.created_by.name }}</template>
      </data-field>
    </p-data-list>
  </entity-list-layout>
</template>

<i18n lang="yaml">
ja:
  field:
    status: 通信状態
    identifier: 識別子
    name: センサー名
    type: 種別
    place: 設置場所
    installation_date: 設置日
    threshold: しきい値
    organization_name: 組織名
    created_by: 作成者
    bookmarked: よく見るセンサー
  search: 文字列で絞り込む
  active_status_filter:
    aria_label: モニタリングポイントの監視状態で絞り込み
    active: 監視中
    inactive: 休止中
    all: すべて
  inactive_suffix: （休止中）

en:
  field:
    status: Status
    identifier: Identifier
    name: Sensor name
    type: Type
    place: Installation site
    installation_date: Installation date
    threshold: Threshold
    organization_name: Organization Name
    created_by: Created by
    bookmarked: Bookmark
  search: Filter by text
  active_status_filter:
    aria_label: Filter monitoring points by active status
    active: Active
    inactive: Inactive
    all: All
  inactive_suffix: (Inactive)
</i18n>

<script>
import Features from '@/mixins/features';
import SensorTypes from '@/mixins/sensorTypes';
import DateTimeFormatting from '@/mixins/dateTimeFormatting';

import EntityListLayout from '@/components/atoms/EntityListLayout';
import SensorTypeLabel from '@/components/atoms/SensorTypeLabel';
import ToggleIconButton from '@/components/atoms/ToggleIconButton';
import SensorStatusIndicator from '@/components/molecules/SensorStatusIndicator';
import DataList, { Field } from '../DataList';
import CreateSensorButton from './CreateSensorButton';
import { applyActiveStatusFilter as applyActiveStatusFilterFn } from './applyActiveStatusFilter';

export default {
  name: 'sensor-list',
  components: {
    CreateSensorButton,
    DataField: Field,
    EntityListLayout,
    PDataList: DataList,
    SensorStatusIndicator,
    SensorTypeLabel,
    ToggleIconButton,
  },
  mixins: [
    Features,
    SensorTypes,
    DateTimeFormatting,
  ],
  beforeMount() {
    this.$store.dispatch('requestSensors');
  },
  computed: {
    sensors() {
      let sensors = this.$store.getters.getSensors.map((sensor) => ({
        ...sensor,
        threshold: String(sensor.threshold),
        type_index: this.SENSOR_TYPE_INDEXES[sensor.type],
        _identifier_sort_key: this.displayedIdentifier(sensor),
      }));
      sensors = this.filterSensors(sensors);
      sensors = this.applyActiveStatusFilter(sensors);
      // 内部 id は登録日時から生成され識別子と順序が一致しないため、識別子で並べる。
      // 識別子が無い休止中の行（移設元）は lodash の比較で種別の末尾に来る
      sensors = this._.sortBy(sensors, ['type_index', 'device_id', 'id']);
      return sensors;
    },
    creatable() {
      const excepts = ['station', 'mirakun_device'];
      const { allowed_sensor_types: allowedSensorTypes } = this.$store.getters.getCurrentUser;
      const availableSensorTypes = this._.without(allowedSensorTypes, ...excepts);
      return availableSensorTypes.length > 0;
    },
  },
  data: function () {
    return {
      pagination: {
        page: 1,
        itemsPerPage: 20,
        sortBy: 'type_index',
      },
      search: '',
      activeStatusFilter: 'active',
    };
  },
  watch: {
    activeStatusFilter() {
      this.pagination.page = 1;
    },
  },
  methods: {
    filterSensors(sensors) {
      const lowerSearchText = this.search.toLowerCase();
      return sensors.filter((sensor) => {
        const { name: userName } = sensor.created_by;
        let { organization_name: organizationName } = sensor.created_by;
        organizationName = organizationName || '';

        const keywords = lowerSearchText.split(' ');
        // eslint-disable-next-line arrow-body-style
        return this._.every(keywords, (keyword) => {
          return this._.some([
            this.displayedIdentifier(sensor),
            sensor.name,
            sensor.place,
            sensor.installedOnText,
            sensor.thresholdText,
            userName,
            organizationName,
            this.SENSOR_TYPE_NAMES[sensor.type],
          ], (value = '') => {
            const lowerValue = (value || '').toLowerCase();
            return this._.includes(lowerValue, keyword);
          });
        });
      });
    },
    applyActiveStatusFilter(sensors) {
      return applyActiveStatusFilterFn(sensors, {
        featureActive: this.featureIsActive('monitoring_point'),
        activeStatusFilter: this.activeStatusFilter,
      });
    },
    genItemProps(item, props) {
      if (item.no_response) {
        props.class = `${props.class || ''} sensor--warning`;
      }
      if (this.featureIsActive('monitoring_point') && item.status === 'inactive') {
        props.class = `${props.class || ''} sensor--inactive`;
      }
      return props;
    },
    showsIdentifier(item) {
      return !(this.featureIsActive('monitoring_point') && item.status === 'inactive');
    },
    displayedIdentifier(item) {
      return this.showsIdentifier(item) ? (item.device_id || '') : '';
    },
    updateBookmarked: async function (sensorId, value) {
      await this.$store.dispatch('requestUpdateSensor', { id: sensorId, data: { bookmarked: value } });
    },
  },
  filters: {
    threshold(sensor) {
      if (['null', 'undefined'].includes(sensor.threshold)) {
        return '-';
      }
      return sensor.threshold;
    },
    thresholdStyle(sensor) {
      if (['null', 'undefined'].includes(sensor.threshold)) {
        return 'text-align: center;';
      }
      return 'text-align: right;';
    },
  },
};
</script>

<style scoped lang="sass">
@import 'vuetify/src/styles/styles.sass'

.sensor-list
  &__sensor-id,
  &__sensor-name,
  &__installation-site
    overflow-wrap: anywhere

  ::v-deep .sensor--inactive
    background-color: rgba(0, 0, 0, 0.04)
    color: rgba(0, 0, 0, 0.5)

    a
      color: rgba(0, 0, 0, 0.5)

  ::v-deep .column
    &--no
      width: 48px

    &--status
      width: 60px
      min-width: 60px
      max-width: 60px

    &--bookmarked
      min-width: 80px
      max-width: 80px

    &--type
      min-width: 180px
      max-width: 180px

    &--id,
    &--name
      min-width: 180px

    &--name
      width: 100%

    &--installed-on
      min-width: 130px

    &--threshold
      min-width: 100px

    &--place,
    &--organization,
    &--user
      width: 180px
      min-width: 140px
</style>
